/**
 * WCAG 2.x contrast utilities for honor-if-set group theming.
 * Ensures restaurant group custom colors are only applied when they produce
 * readable text — otherwise falls back to design-system tokens.
 */

/** Parse a hex color string into [r, g, b] in 0-255 range.
 *  Accepts: 3-digit, 6-digit, 8-digit (alpha ignored), with or without '#'.
 *  Returns null for anything unparseable.
 */
function parseHex(color: string): [number, number, number] | null {
  const raw = color.trim().replace(/^#/, '').toLowerCase();

  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return [r, g, b];
  }

  if (raw.length === 6 || raw.length === 8) {
    const r = parseInt(raw.substring(0, 2), 16);
    const g = parseInt(raw.substring(2, 4), 16);
    const b = parseInt(raw.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return [r, g, b];
  }

  return null;
}

/** sRGB linearization per WCAG 2.x. */
function linearize(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance of a color per WCAG 2.x (0..1). */
function relativeLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb;
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * WCAG 2.x relative-luminance contrast ratio between two colors (1..21).
 * Both colorA and colorB must be parseable hex strings.
 */
export function contrastRatio(colorA: string, colorB: string): number {
  const rgbA = parseHex(colorA);
  const rgbB = parseHex(colorB);

  if (!rgbA || !rgbB) {
    throw new Error(`contrastRatio: unparseable color — "${colorA}", "${colorB}"`);
  }

  const lA = relativeLuminance(rgbA);
  const lB = relativeLuminance(rgbB);
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Returns `candidate` only if it renders readably against `textColor`
 * (WCAG contrast >= 4.5, the AA threshold for normal text); otherwise `fallback`.
 * Null/empty/unparseable candidate → fallback.
 */
export function safeColor(
  candidate: string | null | undefined,
  textColor: string,
  fallback: string,
): string {
  if (!candidate) return fallback;

  const rgb = parseHex(candidate);
  if (!rgb) return fallback;

  try {
    const ratio = contrastRatio(candidate, textColor);
    return ratio >= 4.5 ? candidate : fallback;
  } catch {
    return fallback;
  }
}
